import React from "react";
import T from "prop-types";
import { FieldTitle } from "react-admin";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import FormGroup from "@material-ui/core/FormGroup";
import Checkbox from "@material-ui/core/Checkbox";
import Drawer from "@material-ui/core/Drawer";
import Box from "@material-ui/core/Box";
import DragHandleIcon from "@material-ui/icons/DragHandle";

import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";

import { SelectAllCheckbox } from "../inputs/SelectAllCheckbox";

export const EXCLUDE = -1;

const rowStyle = { display: "flex", alignItems: "center", marginBottom: 4, borderRadius: 4 };
const dragHandleStyle = { display: "flex", alignItems: "center", cursor: "grab", padding: "0 10px", color: "#bbb" };

const SelectAllGridColumns = ({ columns, selection, setAllColumnsExcluded, style }) => {
    const selectedCount = columns.filter(({ source }) => selection[source] !== EXCLUDE).length;

    return (
        <SelectAllCheckbox
            selectedCount={selectedCount}
            totalCount={columns.length}
            onToggle={(shouldSelectAll) => setAllColumnsExcluded(!shouldSelectAll)}
            style={style}
        />
    );
};

SelectAllGridColumns.propTypes = {
	columns: T.arrayOf(T.shape({ source: T.string.isRequired })).isRequired,
	selection: T.object.isRequired,
	setAllColumnsExcluded: T.func.isRequired,
	style: T.object,
};

export const ColumnSelectionForm = (props) => {
    const { columns, selection, resource, filterColumns, formGroupProps = {}, onReorder, setAllColumnsExcluded } = props;

    const onColumnClicked = ({ target: { value: columnName } }) => {
        props.onColumnClicked(columnName);
    };

    const filteredColumns = filterColumns
        ? columns.filter(({ name }) => filterColumns.includes(name))
        : columns;

    // Drag and Drop 
    const handleDragEnd = (result) => {
        if (!result.destination) return;

        const newColumns = Array.from(filteredColumns);
        const [reorderedItem] = newColumns.splice(result.source.index, 1);
        newColumns.splice(result.destination.index, 0, reorderedItem);

        // Notify parent to update state
        if (onReorder) {
            onReorder(newColumns);
        }
    };

    // If no onReorder prop is passed, render the old static list
    if (!onReorder) {
        return (
            <FormGroup {...formGroupProps}>
                {setAllColumnsExcluded && (
                    <SelectAllGridColumns
                        columns={filteredColumns}
                        selection={selection}
                        setAllColumnsExcluded={setAllColumnsExcluded}
                    />
                )}
                {filteredColumns.map(({ source, label }) => (
                    <FormControlLabel
                        key={source}
                        control={
                            <Checkbox
                                checked={selection[source] !== EXCLUDE}
                                onChange={onColumnClicked}
                                value={source}
                            />
                        }
                        label={<FieldTitle label={label} source={source} resource={resource} />}
                    />
                ))}
            </FormGroup>
        );
    }

    // --- Draggable Render ---
    return (
        <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="column-selection-list">
                {(provided) => (
                    <FormGroup
                        {...formGroupProps}
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                    >
                        {setAllColumnsExcluded && (
                            <Box style={rowStyle}>
                                <div style={{ ...dragHandleStyle, visibility: "hidden" }}>
                                    <DragHandleIcon />
                                </div>
                                <SelectAllGridColumns
                                    columns={filteredColumns}
                                    selection={selection}
                                    setAllColumnsExcluded={setAllColumnsExcluded}
                                    style={{ margin: 0 }}
                                />
                            </Box>
                        )}
                        {filteredColumns.map(({ source, label }, index) => (
                            <Draggable key={source} draggableId={source} index={index}>
                                {(provided, snapshot) => (
                                    <Box
                                        ref={provided.innerRef}
                                        {...provided.draggableProps}
                                        style={{
                                            ...rowStyle,
                                            background: snapshot.isDragging ? "#f0f0f0" : "transparent",
                                            ...provided.draggableProps.style,
                                        }}
                                    >
                                        <div {...provided.dragHandleProps} style={dragHandleStyle}>
                                            <DragHandleIcon />
                                        </div>

                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={selection[source] !== EXCLUDE}
                                                    onChange={onColumnClicked}
                                                    value={source}
                                                />
                                            }
                                            label={<FieldTitle label={label} source={source} resource={resource} />}
                                            style={{ margin: 0 }}
                                        />
                                    </Box>
                                )}
                            </Draggable>
                        ))}
                        {provided.placeholder}
                    </FormGroup>
                )}
            </Droppable>
        </DragDropContext>
    );
};

ColumnSelectionForm.propTypes = {
	columns: T.arrayOf(
		T.shape({
			label: T.string,
			source: T.string.isRequired,
		})
	).isRequired,
	selection: T.object.isRequired,
	onColumnClicked: T.func.isRequired,
	resource: T.string.isRequired,
	filterColumns: T.arrayOf(T.string), // Optional prop
	onReorder: T.func,
	setAllColumnsExcluded: T.func,
};

export const SelectionDialog = (props) => {
    const { columns, selection, onClose, resource, configurationLabel, filterColumns, onReorder, setAllColumnsExcluded } = props;

    return (
        <Drawer maxWidth="xs" anchor="left" onClose={onClose} open>
            <DialogTitle id="ra-columns-dialog-title">{configurationLabel}</DialogTitle>
            <DialogContent>
                <ColumnSelectionForm
                    columns={columns}
                    selection={selection}
                    onColumnClicked={props.onColumnClicked}
                    resource={resource}
                    filterColumns={filterColumns}
                    onReorder={onReorder}
                    setAllColumnsExcluded={setAllColumnsExcluded}
                />
            </DialogContent>
        </Drawer>
    );
};

SelectionDialog.propTypes = {
	columns: T.arrayOf(
		T.shape({
			label: T.string,
			source: T.string.isRequired,
		})
	).isRequired,
	selection: T.object.isRequired,
	onColumnClicked: T.func.isRequired,
	onClose: T.func.isRequired,
	resource: T.string.isRequired,
	configurationLabel: T.string.isRequired,
	filterColumns: T.arrayOf(T.string),
	onReorder: T.func,
	setAllColumnsExcluded: T.func,
};

SelectionDialog.defaultProps = {
	columns: [],
};
